import { useEffect, useRef, useState } from "react";
import {
  PRODUCT_VIEWS,
  ProductsViewModel,
} from "../../../viewmodels/setup/myProducts/ProductsViewModel";
import { connect } from "react-redux";

import * as actions from "../../../store/actions/index";
import { formatDate, withRouter } from "../../../shared/utility";
import SlideOver from "../../../components/slideOver/SlideOver";
import EmptyModal from "../../../components/modals/EmptyModal";
import ActionButton from "../../../components/controls/ActionButton";
import ActionLink from "../../../components/controls/ActionLink";
import { auditHistoriesSetupAction } from "../../../components/audit/auditEntityConfig";
import SetupTabs from "../../../components/setup/SetupTabs";
import {
  SetupActions,
  SetupPage,
  SetupTable,
  SetupTd,
} from "../../../components/setup/SetupPage";

const ACTIVE_COLUMNS = ["PLU", "Name", "Description", "Status"];
const DELETED_COLUMNS = ["PLU", "Name", "Description", "Deleted"];

function ProductStatusCell({ product }) {
  if (product.quickbooks_id) {
    return (
      <span className="inline-flex shrink-0 items-center whitespace-nowrap rounded-full bg-[#ECFDF5] px-2.5 py-1 text-xs font-semibold leading-5 text-[#16A34A] ring-1 ring-inset ring-[#A7F3D0]">
        QuickBooks linked
      </span>
    );
  }

  return (
    <span className="inline-flex shrink-0 items-center whitespace-nowrap rounded-full bg-[#FFFBEB] px-2.5 py-1 text-xs font-semibold leading-5 text-[#B45309] ring-1 ring-inset ring-[#FDE68A]">
      Missing QuickBooks ID
    </span>
  );
}

function Products(props) {
  const { addSnackbar, logout } = props;
  const {
    askDeleteProduct,
    askRestoreProduct,
    emptyModal,
    setEmptyModelOpen,
    getProducts,
    fetchProductCount,
    loading,
    products,
    productCounts,
    restoringProductId,
    setSlideOver,
    slideOver,
    openImportProduct,
    addInventory,
    fetchProductsForDropdown,
    openQbLookup,
    qbLookupLoadingProductId,
  } = ProductsViewModel(logout, addSnackbar);

  const [activeTab, setActiveTab] = useState(PRODUCT_VIEWS.active);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(20);
  const searchRef = useRef();
  const currentPageRef = useRef(1);

  const isDeletedView = activeTab === PRODUCT_VIEWS.deleted;
  const tableColumns = isDeletedView ? DELETED_COLUMNS : ACTIVE_COLUMNS;

  useEffect(() => {
    getProducts(null, currentPageRef.current, PRODUCT_VIEWS.active);
    fetchProductCount(PRODUCT_VIEWS.deleted);
    fetchProductsForDropdown();
  }, []);

  const handleTabChange = (tab) => {
    currentPageRef.current = 1;
    setFirst(0);
    setActiveTab(tab);
    getProducts(searchRef.current?.value ?? null, 1, tab);
  };

  const handleSearch = () => {
    currentPageRef.current = 1;
    setFirst(0);
    getProducts(searchRef.current?.value ?? null, 1, activeTab);
  };

  const handlePagination = (event) => {
    currentPageRef.current = event.page + 1;
    setFirst(event.first);
    setRows(event.rows);
    getProducts(
      searchRef.current?.value ?? null,
      currentPageRef.current,
      activeTab,
    );
  };

  const productTabs = [
    {
      id: PRODUCT_VIEWS.active,
      label: `Active (${productCounts.active})`,
    },
    {
      id: PRODUCT_VIEWS.deleted,
      label: `Deleted (${productCounts.deleted})`,
    },
  ];

  const activeActions = [
    { label: "Import", onClick: openImportProduct, variant: "green" },
    { label: "Add Product", href: "/my-products/new" },
    { label: "Add Inventory", onClick: addInventory, variant: "green" },
    auditHistoriesSetupAction("product"),
  ];

  const deletedActions = [auditHistoriesSetupAction("product")];

  return (
    <div>
      <SetupPage
        title="Products"
        description={
          isDeletedView
            ? "Review soft-deleted products and restore them when needed."
            : "Manage finished goods, PLUs, descriptions, QuickBooks status, and inventory setup."
        }
        headerExtra={
          <SetupTabs
            tabs={productTabs}
            value={activeTab}
            onChange={handleTabChange}
          />
        }
        searchPlaceholder="Search by product name or PLU"
        searchRef={searchRef}
        onSearch={handleSearch}
        actions={isDeletedView ? deletedActions : activeActions}
        loading={loading}
        tableColumns={tableColumns}
        totalRecords={products.total}
        first={first}
        rows={rows}
        onPageChange={handlePagination}
      >
        <SetupTable
          columns={tableColumns}
          emptyText={
            isDeletedView ? "No deleted products." : "No products found."
          }
        >
          {products.data?.map((product, index) => (
            <tr
              key={product.id}
              className={`transition hover:bg-[#F6F8FB]/80 ${
                isDeletedView
                  ? "opacity-80"
                  : !product.quickbooks_id
                    ? "bg-[#FFFBEB]/50"
                    : ""
              }`}
            >
              <SetupTd muted={isDeletedView}>{product.plu}</SetupTd>
              <SetupTd muted={true}>{product.name_en}</SetupTd>
              <SetupTd muted={true} className="max-w-md truncate">
                {product.description_en}
              </SetupTd>
              <SetupTd muted={true} className="whitespace-nowrap">
                {isDeletedView ? (
                  <span className="inline-flex shrink-0 items-center whitespace-nowrap rounded-full bg-[#FEF2F2] px-2.5 py-1 text-xs font-semibold leading-5 text-[#DC2626] ring-1 ring-inset ring-[#FECACA]">
                    {product.deleted_at
                      ? `Deleted ${formatDate(product.deleted_at)}`
                      : "Deleted"}
                  </span>
                ) : (
                  <ProductStatusCell product={product} />
                )}
              </SetupTd>
              <SetupActions>
                {isDeletedView ? (
                  <>
                    <ActionButton
                      color="green"
                      onClick={() => askRestoreProduct(product)}
                      loading={restoringProductId === product.id}
                    >
                      Restore
                    </ActionButton>
                  </>
                ) : (
                  <>
                    <ActionLink
                      color="purple"
                      to={`/my-products/${product.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Edit
                    </ActionLink>
                    <ActionButton
                      color="blue"
                      onClick={() => openQbLookup(product)}
                      className="ml-2"
                      disabled={!product.quickbooks_id}
                      loading={qbLookupLoadingProductId === product.id}
                      aria-label={
                        product.quickbooks_id
                          ? "Look up product in QuickBooks"
                          : "QuickBooks lookup unavailable - no QuickBooks ID"
                      }
                    >
                      QB lookup
                    </ActionButton>
                    <ActionButton
                      color="red"
                      onClick={() => askDeleteProduct(product, index)}
                      className="ml-2"
                    >
                      Delete
                    </ActionButton>
                  </>
                )}
              </SetupActions>
            </tr>
          ))}
        </SetupTable>
      </SetupPage>
      <EmptyModal
        childComponent={emptyModal.childComponent}
        open={emptyModal.open}
        setOpen={setEmptyModelOpen}
      />
      <SlideOver
        childComponent={slideOver.childComponent}
        slideOver={slideOver}
        setSlideOver={setSlideOver}
      />
    </div>
  );
}

const mapStateToProps = (state) => {
  return {};
};

const mapDispatchToProps = (dispatch) => {
  return {
    logout: () => dispatch(actions.logout()),
    addSnackbar: (snackbar) => dispatch(actions.addSnackbar(snackbar)),
  };
};

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(Products),
);
